import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import CustomerForm from './CustomerForm';
import { Customer, CreateCustomer, UpdateCustomer, Tag } from '../types';
import { customersAPI, tagsAPI } from '../services/api';
import { navigateWithRedirect } from '../utils/redirectUtils';

const CustomerEditPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<Customer | undefined>(undefined);
  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch tags
        const tagsData = await tagsAPI.getTags();
        setTags(tagsData);

        // Fetch customer if editing
        if (id) {
          const customerData = await customersAPI.getCustomer(parseInt(id));
          setCustomer(customerData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (customerData: CreateCustomer | UpdateCustomer) => {
    setIsLoading(true);
    setError(null);
    try {
      if ('id' in customerData) {
        // Update existing customer
        await customersAPI.updateCustomer(customerData.id, customerData);
      } else {
        // Create new customer
        await customersAPI.createCustomer(customerData);
      }
      // Navigate back to redirect URL or default to customers list
      navigateWithRedirect(navigate, '/customers', searchParams);
    } catch (error) {
      console.error('Error saving customer:', error);
      setError('Failed to save customer');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Navigate back to redirect URL or default to customers list
    navigateWithRedirect(navigate, '/customers', searchParams);
  };

  const handleNewTagCreated = (newTag: Tag) => {
    setTags(prev => [...prev, newTag]);
  };

  if (loading) {
    return (
      <div className="fade-in">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div className="spinner-border" role="status"></div>
          <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div className="container-fluid">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <div className="row">
          <div className="col-12">
            <CustomerForm
              customer={customer}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isLoading={isLoading}
              onNewTagCreated={handleNewTagCreated}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerEditPage;
